/**
 * Canonical values for the Analytics (alert) configuration UI.
 *
 * `value` is what gets stored in alert_configs.event_type and what the command-center's
 * alertEvaluator matches against — i.e. the event's canonical type (classifyEvent uses the
 * sub_type when present, e.g. a "person/crossing" event matches `crossing`). `'*'` matches
 * any event type. The field is a free-typeable datalist, so an AI module emitting a type
 * not listed here can still be targeted by typing it in.
 */
export interface Option {
  value: string;
  label: string;
}

export const EVENT_TYPE_OPTIONS: Option[] = [
  { value: '*', label: 'All event types' },
  { value: 'crossing', label: 'Person crossing' },
  { value: 'loitering', label: 'Person loitering' },
  { value: 'person_counting', label: 'Person counting' },
  { value: 'zone_breach', label: 'Intrusion (zone breach)' },
  { value: 'face_match', label: 'Face match (known)' },
  { value: 'unknown_face', label: 'Unknown face' },
  { value: 'anpr_inout_match', label: 'Vehicle crossing (ANPR)' },
  { value: 'ppe_violation', label: 'PPE violation' },
  { value: 'shoplifting', label: 'Theft / shoplifting' },
  { value: 'fall_detected', label: 'Fall detected' },
  // 'fire_detected' is what migration 022 seeds as the shipped Fire / smoke rule
  // and is the value an existing install already has stored, so it stays the one
  // offered here. 'smoke_detected' is separate because a site may well want a
  // quieter rule for smoke than for flame — both route to the same report.
  { value: 'fire_detected', label: 'Fire detected' },
  { value: 'smoke_detected', label: 'Smoke detected' },
];

export const SEVERITY_OPTIONS: Option[] = [
  { value: '*', label: 'Auto (rule decides)' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'normal', label: 'Normal / info' },
];

/** Scope of a rule: all cameras, a single camera, or a whole group. */
export type RuleScope = 'all' | 'camera' | 'group';

/** Derive the scope of an existing config row from its camera_id/group_id. */
export function scopeOf(cfg: { camera_id?: string | null; group_id?: string | null }): RuleScope {
  if (cfg.camera_id) return 'camera';
  if (cfg.group_id) return 'group';
  return 'all';
}

/** Split a stored event_type (comma-separated = multiple types) into its tokens. */
export const parseEventTypes = (value?: string | null): string[] =>
  String(value ?? '').split(',').map((t) => t.trim()).filter(Boolean);

/** Human label for a stored event_type — handles single, '*', and comma-separated lists. */
export const eventTypeLabel = (value?: string | null): string => {
  const tokens = parseEventTypes(value);
  if (tokens.length === 0) return 'All event types';
  return tokens.map((t) => EVENT_TYPE_OPTIONS.find((o) => o.value === t)?.label || t).join(', ');
};
